import { SABRE_BEDBANK_SUPPLIER_CODE } from 'constants/bedbank'
import { ELIGIBLE_FOR_HOTEL_LOYALTY_POINTS_INCLUSION_DESCRIPTION } from 'lib/search/constants'

/** Supplier badge inclusion shown alongside hotel loyalty program inclusions. */
export const HOTEL_LOYALTY_SUPPLIER_BADGE_INCLUSION = 'SB'

const MEAL_INCLUSION_SYMBOLS = new Set<App.OfferFeatureSymbol>([
  'apple',
  'breakfast',
  'cloche',
  'coffee',
  'crossed_utensils',
  'crossed_utensils_with_plus',
  'drink_tray',
  'juice',
  'martini_glass',
  'wine_glass',
  'wine_glass_and_beer_glass',
])

export type HotelLoyaltyBedbankRate = {
  supplierCode?: string
  bedbankSupplierCode?: string
  inclusions?: Array<{ description: string }>
}

export type HotelLoyaltyFeatureFlags = {
  isHotelLoyaltyEnabled: boolean
  sabreHotelsEnabled: boolean
}

export function getBedbankSupplierCode(rate: Pick<HotelLoyaltyBedbankRate, 'supplierCode' | 'bedbankSupplierCode'>): string | undefined {
  return rate.supplierCode ?? rate.bedbankSupplierCode
}

export function isSabreBedbankSupplier(supplierCode?: string): boolean {
  return supplierCode === SABRE_BEDBANK_SUPPLIER_CODE
}

export function hasHotelLoyaltyPointsInclusion(
  inclusions?: Array<{ description: string }>,
): boolean {
  return inclusions?.some(
    inclusion => inclusion.description === ELIGIBLE_FOR_HOTEL_LOYALTY_POINTS_INCLUSION_DESCRIPTION,
  ) ?? false
}

export function hasHotelLoyaltyInclusionMarkersForDisplay(
  inclusions?: Array<{ description: string }>,
): boolean {
  if (!inclusions?.length) {
    return false
  }

  return inclusions.some(
    inclusion =>
      inclusion.description === ELIGIBLE_FOR_HOTEL_LOYALTY_POINTS_INCLUSION_DESCRIPTION
      || inclusion.description === HOTEL_LOYALTY_SUPPLIER_BADGE_INCLUSION,
  )
}

/**
 * Resolves the program name to strip from inclusion lists when loyalty marker or SB badge
 * inclusions are present, including for rates that are not Sabre-eligible.
 */
export function getLoyaltyProgramNameForInclusionDisplayFilter(
  inclusions?: Array<Pick<App.OfferInclusion, 'description' | 'symbol'>>,
): string | undefined {
  if (!hasHotelLoyaltyInclusionMarkersForDisplay(inclusions)) {
    return undefined
  }

  return getHotelLoyaltyProgramNameFromRateInclusions(inclusions)
}

function isHotelLoyaltyInclusionHiddenFromDisplay(
  description: string,
  loyaltyProgramName?: string,
): boolean {
  if (description === ELIGIBLE_FOR_HOTEL_LOYALTY_POINTS_INCLUSION_DESCRIPTION) {
    return true
  }

  if (description === HOTEL_LOYALTY_SUPPLIER_BADGE_INCLUSION) {
    return true
  }

  if (loyaltyProgramName && description === loyaltyProgramName) {
    return true
  }

  return false
}

/**
 * Removes hotel loyalty marker, supplier badge, and program name inclusions from rate
 * display lists. Loyalty messaging is shown via the earn label for Sabre rates only.
 */
export function filterHotelLoyaltyInclusionsForDisplay<T extends { description: string }>(
  rateInclusions: Array<T>,
  options?: { loyaltyProgramName?: string },
): Array<T> {
  return rateInclusions.filter(
    inclusion => !isHotelLoyaltyInclusionHiddenFromDisplay(inclusion.description, options?.loyaltyProgramName),
  )
}

function isMealInclusion(
  inclusion: Pick<App.OfferInclusion, 'description' | 'symbol'>,
): boolean {
  return !!(inclusion.symbol && MEAL_INCLUSION_SYMBOLS.has(inclusion.symbol))
}

/**
 * Returns the hotel chain loyalty program name from rate inclusions (e.g. "Hilton Honors").
 *
 * Sabre loyalty-eligible rates typically include (after board/meal inclusions):
 *   1. "Eligible for hotel loyalty points"
 *   2. Program name (e.g. "Hilton Honors") — may have other non-meal inclusions nearby
 *   3. "SB" supplier badge
 *
 * Resolution walks backwards from the SB badge (or end of list), skips meal inclusions,
 * and stops at the eligibility marker so the program name is taken from between those two.
 * Returns undefined when the program name inclusion is missing or misordered; callers should
 * fall back to a generic earn label (see `formatHotelLoyaltyPointsEarnLabel`).
 */
export function getHotelLoyaltyProgramNameFromRateInclusions(
  inclusions?: Array<Pick<App.OfferInclusion, 'description' | 'symbol'>>,
): string | undefined {
  if (!inclusions?.length) {
    return undefined
  }

  const badgeIndex = inclusions.findIndex(
    inclusion => inclusion.description === HOTEL_LOYALTY_SUPPLIER_BADGE_INCLUSION,
  )
  const searchEnd = badgeIndex === -1 ? inclusions.length : badgeIndex

  for (let index = searchEnd - 1; index >= 0; index--) {
    const inclusion = inclusions[index]

    if (inclusion.description === ELIGIBLE_FOR_HOTEL_LOYALTY_POINTS_INCLUSION_DESCRIPTION) {
      break
    }

    if (isMealInclusion(inclusion)) {
      continue
    }

    return inclusion.description
  }

  return undefined
}

export function isBedbankRateEligibleForHotelLoyaltyProgram(
  rate: HotelLoyaltyBedbankRate,
  flags: HotelLoyaltyFeatureFlags,
): boolean {
  if (!flags.isHotelLoyaltyEnabled || !flags.sabreHotelsEnabled) {
    return false
  }

  const supplierCode = getBedbankSupplierCode(rate)

  return isSabreBedbankSupplier(supplierCode) && hasHotelLoyaltyPointsInclusion(rate.inclusions)
}

export function isSearchTileEligibleForHotelLoyaltyProgram(
  options: {
    rates?: Array<HotelLoyaltyBedbankRate>
  },
  flags: HotelLoyaltyFeatureFlags,
): boolean {
  if (!flags.isHotelLoyaltyEnabled || !flags.sabreHotelsEnabled) {
    return false
  }

  return options.rates?.some(rate => isBedbankRateEligibleForHotelLoyaltyProgram(rate, flags)) ?? false
}

export function isPropertyRateLoyaltyEligible(
  rate: App.PropertyRate,
  isLoyaltyProgramFeatureFlagEnabled: boolean,
  sabreHotelsEnabled: boolean,
): boolean {
  if (rate.type !== 'bedbank') {
    return false
  }

  return isBedbankRateEligibleForHotelLoyaltyProgram(
    { bedbankSupplierCode: rate.bedbankSupplierCode, inclusions: rate.inclusions },
    {
      isHotelLoyaltyEnabled: isLoyaltyProgramFeatureFlagEnabled,
      sabreHotelsEnabled,
    },
  )
}
